// RAG-API Response Types (matching backend models)

export interface ApiDocumentTag {
  id: string
  tag_id: string
  tag_name: string
  tag_type?: string
  created_at: string
  created_by: string
}

export type ApiDocumentStatus = 'pending' | 'processing' | 'embedded' | 'failed'

export interface ApiDocumentStatusDisplay {
  code: string
  state: 'processing' | 'processed' | 'failed'
  i18n_key: string
  label: string
}

export interface ApiDocument {
  id: number
  title: string
  description?: string
  document_type: 'pdf' | 'url' | 'txt' | 'csv' | 'doc'
  url?: string
  file?: string // File URL/path from backend
  download_url?: string // Canonical download endpoint from backend
  file_size_bytes?: number | null
  // Compatibility with older API payloads observed in some environments.
  file_size?: number | string | null
  size_bytes?: number | string | null
  size?: number | string | null
  status: ApiDocumentStatus
  status_display?: ApiDocumentStatusDisplay
  category?: number // Foreign key to category
  category_name?: string // Populated from category relationship
  subcategory?: number // Foreign key to subcategory  
  subcategory_name?: string // Populated from subcategory relationship
  directory?: number | null // Optional FK to directory
  directory_name?: string | null
  directory_path?: string | null // Full path e.g. "Parent / Child"
  tags?: ApiDocumentTag[] // Document tags from RAG API
  tag_count?: number // Number of tags assigned
  created_at: string
  updated_at: string
  last_processed_at?: string
  last_updated_at?: string
  position?: number
  metadata?: Record<string, unknown>
  docling_processed?: boolean
  processing_result?: unknown
  processing_error?: string
}

export interface ApiCategory {
  id: number
  name: string
  description?: string
  created_at: string
  updated_at: string
  subcategories?: ApiSubcategory[]
}

export interface ApiSubcategory {
  id: number
  name: string
  description?: string
  category: number // Foreign key to category
  category_name?: string
  created_at: string
  updated_at: string
}

export interface ApiDirectory {
  id: number
  name: string
  description?: string
  parent?: number | null
  parent_name?: string | null
  position?: number
  level?: number
  children?: ApiDirectory[]
  created_at?: string
}

// Frontend Component Types (transformed from API types for UI consumption)

export interface DocumentTag {
  id: string
  tag_id: string
  tag_name: string
  tag_type?: string
  created_at: string
  created_by: string
}

export interface DocumentStatusDisplay {
  code: string
  state: 'processing' | 'processed' | 'failed'
  i18nKey: string
  label: string
}

export interface Document {
  id: string
  name: string
  title: string
  description: string
  category: string
  subcategory: string
  directory?: string | null
  directoryPath?: string | null
  type: string
  size: string
  status?: ApiDocumentStatus
  statusDisplay?: DocumentStatusDisplay
  url?: string
  /** File path/URL for file-based documents (e.g. documents/files/xyz.pdf or /media/documents/files/xyz.pdf) */
  file?: string
  /** Canonical backend endpoint for authenticated file downloads */
  downloadUrl?: string
  isPublic?: boolean
  addedDate?: string
  lastUpdatedAt?: string
  position?: number
  tags?: DocumentTag[] // Document tags
  tag_count?: number
}

export interface URL {
  id: string
  url: string
  title: string
  description: string
  category: string
  subcategory: string
  directory?: string | null
  directoryPath?: string | null
  isPublic: boolean
  addedDate?: string
  lastUpdatedAt?: string
  position?: number
  status?: ApiDocumentStatus
  statusDisplay?: DocumentStatusDisplay
}

export interface Category {
  id: string
  name: string
  icon?: string
  subcategories: Category[]
  items?: unknown[]
  itemCount: number
}

// Form Data Types

export interface DocumentUploadData {
  title: string
  description?: string
  category?: string
  subcategory?: string
  directory?: string | null
  file?: File
  document_type: 'pdf' | 'txt' | 'csv' | 'doc'
}

export interface URLUploadData {
  title: string
  url: string
  description?: string
  category?: string
  subcategory?: string
  directory?: string | null
  document_type: 'url'
  isPublic?: boolean
  auto_embed?: boolean
}

export interface CategoryCreateData {
  name: string
  description?: string
}

export interface SubcategoryCreateData {
  name: string
  description?: string
  category: number
}

export interface SubcategoryUpdateData {
  name?: string
  description?: string
  category?: number
}

// Chatbot Settings Types
export interface ApiChatbotSettings {
  id: number
  text: string
  is_active: boolean
  ask_email: boolean
  ask_phone: boolean
  ask_name: boolean
  created_at: string
  updated_at: string
}

export interface ChatbotSettings {
  text: string
  isActive: boolean
  askEmail: boolean
  askPhone: boolean
  askName: boolean
}

// Utility Types

export type DocumentType = 'pdf' | 'url' | 'txt' | 'csv'
export type DocumentStatus = ApiDocumentStatus

// Data transformation utilities

export class DataTransformers {
  private static toByteCount(value: unknown): number | null {
    if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
      return value
    }
    if (typeof value === "string") {
      const parsed = Number(value.trim())
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed
      }
    }
    return null
  }

  private static resolveFileSizeBytes(apiDoc: ApiDocument): number | null {
    const directCandidates: unknown[] = [
      apiDoc.file_size_bytes,
      apiDoc.file_size,
      apiDoc.size_bytes,
      apiDoc.size,
    ]
    for (const candidate of directCandidates) {
      const parsed = DataTransformers.toByteCount(candidate)
      if (parsed !== null) return parsed
    }

    const metadata = apiDoc.metadata
    if (metadata && typeof metadata === "object") {
      const record = metadata as Record<string, unknown>
      const metadataCandidates: unknown[] = [
        record.file_size_bytes,
        record.fileSizeBytes,
        record.file_size,
        record.size_bytes,
        record.size,
      ]
      for (const candidate of metadataCandidates) {
        const parsed = DataTransformers.toByteCount(candidate)
        if (parsed !== null) return parsed
      }
    }

    return null
  }

  static formatFileSize(bytes?: number | null): string {
    if (typeof bytes !== "number" || Number.isNaN(bytes) || bytes < 0) return "—"
    if (bytes < 1024) return `${bytes} B`
    const kb = bytes / 1024
    if (kb < 1024) return `${kb.toFixed(kb >= 100 ? 0 : 1)} KB`
    const mb = kb / 1024
    if (mb < 1024) return `${mb.toFixed(mb >= 100 ? 0 : 1)} MB`
    const gb = mb / 1024
    return `${gb.toFixed(gb >= 100 ? 0 : 1)} GB`
  }

  static apiDocumentToDocument(apiDoc: ApiDocument): Document {
    return {
      id: apiDoc.id.toString(),
      name: apiDoc.file ? apiDoc.file.split('/').pop() || apiDoc.title : apiDoc.title,
      title: apiDoc.title,
      description: apiDoc.description || '',
      category: apiDoc.category_name || '',
      subcategory: apiDoc.subcategory_name || '',
      directory: apiDoc.directory ? apiDoc.directory.toString() : null,
      directoryPath: apiDoc.directory_path ?? null,
      type: apiDoc.document_type,
      size: DataTransformers.formatFileSize(DataTransformers.resolveFileSizeBytes(apiDoc)),
      status: apiDoc.status,
      statusDisplay: apiDoc.status_display
        ? {
            code: apiDoc.status_display.code,
            state: apiDoc.status_display.state,
            i18nKey: apiDoc.status_display.i18n_key,
            label: apiDoc.status_display.label,
          }
        : undefined,
      url: apiDoc.url,
      file: apiDoc.file,
      downloadUrl: apiDoc.download_url,
      isPublic: apiDoc.document_type === 'url',
      addedDate: apiDoc.created_at,
      lastUpdatedAt: apiDoc.last_updated_at,
      position: apiDoc.position,
      tags: apiDoc.tags?.map(tag => ({
        id: tag.id.toString(),
        tag_id: tag.tag_id,
        tag_name: tag.tag_name,
        tag_type: tag.tag_type || 'universal',
        created_at: tag.created_at,
        created_by: tag.created_by
      })) || [],
      tag_count: apiDoc.tag_count || 0,
    }
  }

  static apiDocumentToURL(apiDoc: ApiDocument): URL {
    return {
      id: apiDoc.id.toString(),
      url: apiDoc.url || '',
      title: apiDoc.title,
      description: apiDoc.description || '',
      category: apiDoc.category_name || '',
      subcategory: apiDoc.subcategory_name || '',
      directory: apiDoc.directory ? apiDoc.directory.toString() : null,
      directoryPath: apiDoc.directory_path ?? null,
      isPublic: true, // URLs are typically public
      addedDate: apiDoc.created_at,
      lastUpdatedAt: apiDoc.last_updated_at,
      position: apiDoc.position,
      status: apiDoc.status,
      statusDisplay: apiDoc.status_display
        ? {
            code: apiDoc.status_display.code,
            state: apiDoc.status_display.state,
            i18nKey: apiDoc.status_display.i18n_key,
            label: apiDoc.status_display.label,
          }
        : undefined,
    }
  }

  static urlUploadToApiData(data: URLUploadData): object {
    return {
      title: data.title,
      url: data.url,
      description: data.description,
      category_id: data.category ? parseInt(data.category, 10) : undefined,
      subcategory_id: data.subcategory ? parseInt(data.subcategory, 10) : undefined,
      directory: data.directory ? parseInt(data.directory, 10) : undefined,
      auto_embed: true,
    }
  }

  static apiSettingsToSettings(apiSettings: ApiChatbotSettings): ChatbotSettings {
    return {
      text: apiSettings.text,
      isActive: apiSettings.is_active,
      askEmail: apiSettings.ask_email,
      askPhone: apiSettings.ask_phone,
      askName: apiSettings.ask_name,
    }
  }

  static settingsToApiSettings(settings: ChatbotSettings): Partial<ApiChatbotSettings> {
    return {
      text: settings.text,
      is_active: settings.isActive,
      ask_email: settings.askEmail,
      ask_phone: settings.askPhone,
      ask_name: settings.askName,
    }
  }
}

// Error types
export interface ApiError {
  message: string
  status: number
  details?: unknown
}
